import "../../styles/SideNav.css";

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LanIcon from "@mui/icons-material/Lan";
import {
  FaBars,
  FaBalanceScale,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaCodeBranch,
  FaDatabase,
  FaMobile,
  FaBatteryHalf,
  FaSun,
  FaLightbulb,
  FaBolt,
  FaExchangeAlt,
  FaThLarge,
  FaUsers,
  FaUserTie,
  FaCog,
} from "react-icons/fa";
import { GiVial } from "react-icons/gi";
import { FiDatabase, FiGrid, FiSmartphone } from "react-icons/fi";

const iconMap = {
  Lan: LanIcon,
  FaBalanceScale,
  FaBolt,
  FaCodeBranch,
  FaDatabase,
  FaMobile,
  FaBatteryHalf,
  FaSun,
  FaLightbulb,
  FaExchangeAlt,
  FaThLarge,
  FaUsers,
  FaUserTie,
  GiVial,
  FaCog,
};

function resolveIcon(icon) {
  return typeof icon === "string" ? iconMap[icon] : icon;
}

const groupWireIconByLabel = {
  Devices: FiSmartphone,
  Data: FiDatabase,
  Simulations: FiGrid,
};

function normalizePath(path) {
  if (!path) return "";
  const out = String(path).trim();
  if (!out) return "";
  return out.length > 1 ? out.replace(/\/+$/, "") : out;
}

function pathMatches(active, candidate) {
  const a = normalizePath(active);
  const c = normalizePath(candidate);
  if (!a || !c) return false;
  return a === c || a.startsWith(`${c}/`);
}

const SideNav = ({ items = [], title = "Navigation" }) => {
  // Docker-docs style: expanded by default
  const [open, setOpen] = React.useState(true);
  const [openGroups, setOpenGroups] = React.useState(() => ({}));
  const navigate = useNavigate();
  const location = useLocation();

  const toggle = () => setOpen((prev) => !prev);
  const toggleGroup = (label) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const activePath = location.pathname;

  // Ensure only ONE item is active: choose the "best" match (longest matching path).
  const activeNavPath = React.useMemo(() => {
    let best = "";

    for (const item of items || []) {
      if (!item) continue;

      if (item.path && pathMatches(activePath, item.path)) {
        const p = normalizePath(item.path);
        if (p.length > best.length) best = p;
      }

      if (Array.isArray(item.subitems)) {
        for (const sub of item.subitems) {
          if (!sub?.path) continue;
          if (!pathMatches(activePath, sub.path)) continue;
          const p = normalizePath(sub.path);
          if (p.length > best.length) best = p;
        }
      }
    }

    return best;
  }, [activePath, items]);

  return (
    <div className={`sidenav-wrapper ${open ? "open" : "closed"}`}>
      <nav className="sidenav" aria-label={title}>
        <div className="sidenav-header">
          <button
            type="button"
            className="sidenav-toggle"
            onClick={toggle}
            aria-label={open ? "Collapse navigation" : "Expand navigation"}
          >
            {open ? <FaChevronLeft /> : <FaBars />}
          </button>
          {open ? <div className="sidenav-title">{title}</div> : null}
        </div>

        <div className="sidenav-divider" />

        <div className="sidenav-scroll">
          <ul className="sidenav-list list-unstyled m-0 p-0">
            {(items || []).map((item, index) => {
              if (!item) return null;

              // Divider
              if (item.type === "divider") {
                return <li key={`divider-${index}`} className="sidenav-divider-item" aria-hidden="true" />;
              }

              // Top-level links
              if (item.path) {
                const Icon = resolveIcon(item.icon);
                const selected = normalizePath(item.path) === activeNavPath;
                return (
                  <li key={item.label} className="sidenav-item">
                    <button
                      type="button"
                      className={`sidenav-link ${selected ? "active" : ""}`}
                      onClick={() => handleNavigate(item.path)}
                      title={!open ? item.label : undefined}
                      aria-current={selected ? "page" : undefined}
                    >
                      <span className="sidenav-icon" aria-hidden="true">
                        {Icon ? <Icon /> : null}
                      </span>
                      <span className="sidenav-label">{item.label}</span>
                    </button>
                  </li>
                );
              }

              // Group sections (submenus)
              if (Array.isArray(item.subitems) && item.subitems.length > 0) {
                const groupOpen = openGroups[item.label] ?? true;
                const Chevron = groupOpen ? FaChevronDown : FaChevronRight;
                const WireIcon = groupWireIconByLabel[item.label];
                const GroupIcon =
                  WireIcon ||
                  resolveIcon(item.icon) ||
                  resolveIcon(item.subitems?.[0]?.icon);
                const groupClassName = item.className 
                  ? `sidenav-group ${item.className}` 
                  : "sidenav-group";
                return (
                  <li key={item.label} className={groupClassName}>
                    <button
                      type="button"
                      className="sidenav-group-header"
                      onClick={() => toggleGroup(item.label)}
                      title={!open ? item.label : undefined}
                      aria-expanded={groupOpen}
                    >
                      <span className="sidenav-icon sidenav-group-icon" aria-hidden="true">
                        {GroupIcon ? <GroupIcon /> : null}
                      </span>
                      <span className="sidenav-group-title">{item.label}</span>
                      {open ? (
                        <span className="sidenav-group-chevron" aria-hidden="true">
                          <Chevron />
                        </span>
                      ) : null}
                    </button>

                    {groupOpen ? (
                      <ul className="sidenav-sublist list-unstyled m-0 p-0">
                        {item.subitems.map((sub) => {
                          if (!sub?.path) return null;
                          const Icon = resolveIcon(sub.icon);
                          const selected = normalizePath(sub.path) === activeNavPath;
                          return (
                            <li key={`${item.label}:${sub.label}`} className="sidenav-subitem">
                              <button
                                type="button"
                                className={`sidenav-sublink ${selected ? "active" : ""}`}
                                onClick={() => handleNavigate(sub.path)}
                                title={!open ? sub.label : undefined}
                                aria-current={selected ? "page" : undefined}
                              >
                                <span className="sidenav-icon" aria-hidden="true">
                                  {Icon ? <Icon /> : null}
                                </span>
                                <span className="sidenav-label">{sub.label}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              }

              return null;
            })}
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default SideNav;